/**
 * Le icone delle sezioni del pannello del fornitore.
 *
 * **Scritte qui, non installate.** Un pacchetto di icone ne porta dentro
 * centinaia per usarne sette, e ogni dipendenza oltre allo stack deciso va
 * giustificata: il progetto deve restare manutenibile da una persona e girare
 * su un VPS singolo (CLAUDE.md). Sette `<svg>` di tre righe l'uno non sono una
 * dipendenza — sono sette funzioni pure senza aggiornamenti, senza licenza da
 * seguire e senza niente da ricostruire quando cambia la versione maggiore di
 * qualcun altro.
 *
 * **Nessuna di queste è un'etichetta.** Accanto a ognuna c'è sempre il nome
 * della sezione, tradotto, che è il nome accessibile del pulsante: quindi ogni
 * `<svg>` è `aria-hidden` e `focusable="false"` — un secondo nome sull'icona
 * farebbe annunciare la sezione due volte, e un'icona che diventasse l'unica
 * etichetta lascerebbe a piedi chi non vede (sezione 7bis).
 *
 * **Nessun colore proprio.** `currentColor` ovunque: l'icona prende il colore
 * del testo che le sta accanto, quindi passa in scuro senza un secondo token e
 * non può finire fuori contrasto per conto suo.
 */

export type IconProps = { className?: string };

/**
 * Esportato: lo riusa anche `NavIcons.tsx` per le voci dell'ente e del
 * volontario, stessa filosofia — nessuna icona presa da un pacchetto, un
 * involucro solo per non ripetere sette attributi identici in ogni funzione.
 */
export function Icon({ className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      className={className ?? 'icon'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Gli enti: una sagoma di edificio, con due finestre e un corpo più basso. */
export function TenantsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 21V4.5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1V21" />
      <path d="M13.5 10h6a1 1 0 0 1 1 1v10" />
      <path d="M2.5 21h19" />
      <path d="M6.5 8h3M6.5 13h3" />
    </Icon>
  );
}

/** Nuovo ente: lo stesso edificio, con un più. Si legge accanto al primo. */
export function NewTenantIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 21V4.5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1V11" />
      <path d="M2.5 21H10" />
      <path d="M6.5 8h3" />
      <circle cx="17" cy="17" r="4.75" />
      <path d="M17 14.5v5M14.5 17h5" />
    </Icon>
  );
}

/** I domini: un globo con un meridiano, cioè un indirizzo sul web. */
export function DomainsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.4 2.5 3.7 5.6 3.7 9S14.4 18.5 12 21c-2.4-2.5-3.7-5.6-3.7-9S9.6 5.5 12 3Z" />
    </Icon>
  );
}

/** Gli avvisi: una campana. Non un punto esclamativo — quello è l'errore. */
export function AlertsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M18 9a6 6 0 1 0-12 0c0 4.6-2 6.2-2 6.2h16S18 13.6 18 9Z" />
      <path d="M13.7 18.8a2 2 0 0 1-3.4 0" />
    </Icon>
  );
}

/**
 * La blacklist: il segnale di divieto.
 *
 * Non una sagoma di persona sbarrata: qui si bloccano impronte di numeri, non
 * individui, e disegnare una persona cancellata direbbe una cosa più pesante
 * di quella che quella schermata fa.
 */
export function BlacklistIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6 18.4 18.4" />
    </Icon>
  );
}

/** Il monitoraggio: uno schermo con un battito. */
export function HealthIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M6 12.5h2.5L10.5 9l2.5 6.5 1.8-3h3.2" />
    </Icon>
  );
}

/** L'infrastruttura media: tre cluster impilati, ciascuno con la propria spia. */
export function ClustersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="4" width="17" height="4.5" rx="1" />
      <rect x="3.5" y="9.75" width="17" height="4.5" rx="1" />
      <rect x="3.5" y="15.5" width="17" height="4.5" rx="1" />
      <path d="M7 6.25h.01M7 12h.01M7 17.75h.01" />
    </Icon>
  );
}

/**
 * Gli accessi: un orologio che torna indietro.
 *
 * Non una chiave: da quella schermata non si sblocca niente, si legge il
 * registro di chi è entrato e quando.
 */
export function AccessIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.7-6.2" />
      <path d="M3 4v4.5h4.5" />
      <path d="M12 7.5V12l3 1.8" />
    </Icon>
  );
}

/**
 * "Maggiori informazioni": un cerchio con una "i".
 *
 * Usata da `InfoTip.tsx`, non da sola: il nome accessibile del pulsante che
 * la contiene è sempre una traduzione ("Maggiori informazioni"), mai questa
 * icona — stessa regola di tutte le altre.
 */
export function InfoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5" strokeWidth={2} />
      <circle cx="12" cy="8" r="0.25" fill="currentColor" stroke="currentColor" strokeWidth={2} />
    </Icon>
  );
}
